import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { useRef } from "react";
import { WaitlistForm } from "./WaitlistForm";

const HERO_IMG =
  "https://images.unsplash.com/photo-1519315901367-f34ff9154487?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.35 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const Line = ({ children, i }) => (
  <span className="block overflow-hidden">
    <motion.span variants={line} custom={i} initial="hidden" animate="show" className="block">
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.5, 0.9]);

  // Subtle 3D tilt on the framed image
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 18 });
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => { mx.set(0); my.set(0); };

  return (
    <section ref={ref} id="top" data-testid="hero" className="relative min-h-[100svh] overflow-hidden">
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0">
        <img src={HERO_IMG} alt="Swimmer training in an olympic pool" className="h-full w-full object-cover" />
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-ink" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-ink/30" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/80 to-transparent" />
      </motion.div>

      <div className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-end px-6 pb-16 pt-32 md:px-10 md:pb-24">
        <div className="grid items-end gap-12 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.25, duration: 0.8 }}
              className="mb-6 flex items-center gap-3 text-sm font-500 uppercase tracking-[0.3em] text-aqua"
            >
              <span className="h-px w-10 bg-aqua" /> Aqua fitness, reimagined
            </motion.p>

            <h1 className="font-display text-[15vw] font-900 leading-[0.86] tracking-tighter text-textMain sm:text-[13vw] lg:text-[9.5vw]">
              <Line i={0}>MOVE</Line>
              <Line i={1}>
                LIKE <span className="font-serif-italic font-400 text-aqua">water</span>
              </Line>
              <Line i={2}>
                TRAIN <span className="text-stroke-aqua">WEIGHTLESS</span>
              </Line>
            </h1>
          </div>

          <div className="lg:col-span-4">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="mb-7 max-w-sm text-base text-textMuted md:text-lg"
            >
              100+ water exercises on video for every body — from rehab to race day. Zero impact on your joints, total impact on your fitness.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.8 }}
              id="join"
            >
              <WaitlistForm source="hero" testid="hero-waitlist" />
              <p className="mt-3 pl-2 text-xs text-textMuted">Join 12,000+ swimmers on the early-access list.</p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Floating tilt frame */}
      <motion.div
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.1, duration: 1, ease: [0.22, 1, 0.36, 1] }}
        style={{ perspective: 800 }}
        className="pointer-events-auto absolute right-10 top-28 z-10 hidden w-52 xl:block"
      >
        <motion.div
          style={{ rotateX: rotX, rotateY: rotY, transformStyle: "preserve-3d" }}
          className="overflow-hidden rounded-2xl border border-textMuted/25 bg-surface/40 shadow-2xl backdrop-blur-sm"
        >
          <img
            src="https://images.pexels.com/photos/8688566/pexels-photo-8688566.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
            alt="Underwater swimmer"
            className="h-64 w-full object-cover"
          />
          <div className="flex items-center justify-between px-4 py-3">
            <span className="font-serif-italic text-sm text-textMain">Live session</span>
            <span className="h-2 w-2 rounded-full bg-lime shadow-[0_0_10px_#D4FF00]" />
          </div>
        </motion.div>
      </motion.div>
    </section>
  );
};
